import { Injectable } from '@angular/core';
import { ContainerClient, BlobServiceClient } from '@azure/storage-blob';
import { AppConfig } from '../../config/config';
import { ContainerNames } from '../../models/container-names.model';
import { Guid } from 'typescript-guid';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'shared-lib';

// setLogLevel("info");

@Injectable({
  providedIn: 'root'
})
export class BlobStorageService {
    private accountName: string = "caimsmsstorage";
    private containerName: string = '';
    private sasTokens: { [containerName: string]: { token: string, expiresOn: number } } = {};

    constructor(private authService: AuthService) {}

    public setContainer(containerName: ContainerNames) {
        this.containerName = containerName;
    }

    public uploadFile(
        content: Blob, 
        name: string, 
        successHandler: (response: any) => void, 
        errorHandler: (error: any) => void
    ) {
        this.containerClientWithSas()
            .then((client) => this.uploadBlob(
                content,
                `${name}_${Guid.create()}`,
                client,
                successHandler,
                errorHandler
            ))
            .catch((error: any) => errorHandler(error));
    }

    private uploadBlob(
        content: Blob, 
        name: string, 
        client: ContainerClient, 
        successHandler: (response: any) => void, 
        errorHandler: (error: any) => void
    ) {
        const blockBlobClient = client.getBlockBlobClient(name);
        blockBlobClient.uploadData(content, { blobHTTPHeaders: { blobContentType: content.type } })
            .then(() => successHandler(blockBlobClient.url.split('?')[0]))
            .catch((error: any) => errorHandler(error));  // Call error handler if upload fails
    }

    public async listFile(): Promise<string[]> {
        return this.listBlobs(this.containerClient());
    }

    public downloadFile(name: string, handler: (blob: Blob) => void) {
        this.downloadBlob(name, this.containerClient(), handler);
    }

    private async listBlobs(client: ContainerClient): Promise<string[]> {
        const result: string[] = [];
        for await (const blob of client.listBlobsFlat()) {
            result.push(blob.name);
        }
        return result;
    }

    private downloadBlob(name: string, client: ContainerClient, handler: (blob: Blob) => void) {
        const blobClient = client.getBlobClient(name);
        blobClient.download().then((resp: any) => {
            if (resp.blobBody) {
                resp.blobBody.then((blob: any) => handler(blob));
            }
        });
    }

    private containerClient(): ContainerClient {
        return new BlobServiceClient(`https://${this.accountName}.blob.core.windows.net`).getContainerClient(this.containerName);
    }

    private async containerClientWithSas(): Promise<ContainerClient> {
        const containerName = this.containerName;
        const cached = this.sasTokens[containerName];
        if (!cached || cached.expiresOn < Date.now()) {
            const responseData = (await firstValueFrom(this.authService.getSas(containerName))).body;
            if (responseData?.status !== 200 && responseData?.status !== 'OK') {
                throw new Error(responseData?.message);
            }
            this.sasTokens[containerName] = { token: responseData.data, expiresOn: Date.now() + 25 * 60 * 1000 };
        }
        return new BlobServiceClient(`https://${this.accountName}.blob.core.windows.net${this.sasTokens[containerName].token}`).getContainerClient(containerName);
    }
}
